import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { SHOW_SAVE_AS_DIALOG_KEY } from '../utils/saveFile';
import userSettingsService from '../services/userSettings.service';

/**
 * Per-user working/dispatch preferences.
 *
 * Persisted to the backend `user_settings` table under one key, so a preference
 * follows the user to another machine or browser instead of living only where they
 * happened to set it. localStorage is kept as a mirror: it seeds the initial state
 * so the first paint is correct rather than flashing a default and then correcting
 * itself, it keeps working when the settings call fails or the user is not yet
 * authenticated, and `saveFile.ts` reads SHOW_SAVE_AS_DIALOG_KEY straight out of it
 * at download time.
 *
 * Same shape as DisplayPreferencesContext, which stores its own blob the same way.
 */

const SETTING_KEY = 'working_status_preferences';

type PrefName =
  | 'showSamsaraRouteEtas'
  | 'showExtendedWorkingStatus'
  | 'defaultSendToGwiMove'
  | 'planningShowAllMoves'
  | 'planningShowTwic'
  | 'autoCloseProOnSave'
  | 'showSaveAsDialog'
  | 'compactFollowUpHandle'
  | 'hideFollowUpHandleWhenEmpty';

/**
 * localStorage key + default for each preference. Most default OFF; the two that
 * default ON are opt-OUT and only an explicit 'false' disables them.
 */
const PREFS: Record<PrefName, { storageKey: string; defaultValue: boolean }> = {
  showSamsaraRouteEtas: { storageKey: 'showSamsaraRouteEtas', defaultValue: false },
  // Working-category detail on Planned/Accepted status cells.
  showExtendedWorkingStatus: { storageKey: 'showExtendedWorkingStatus', defaultValue: false },
  // Seeds the per-page "Send to gwiMove" toggles on the Dispatch Planner and the
  // Driver Status Monitor. Blank/absent means off.
  defaultSendToGwiMove: { storageKey: 'defaultSendToGwiMove', defaultValue: false },
  planningShowAllMoves: { storageKey: 'planningShowAllMoves', defaultValue: false },
  // Defaults ON: roughly three in four drivers carry a valid TWIC, so the badge is
  // only worth hiding once you have seen it and decided it is noise.
  planningShowTwic: { storageKey: 'planningShowTwic', defaultValue: true },
  autoCloseProOnSave: { storageKey: 'autoCloseProOnSave', defaultValue: false },
  // Defaults ON. saveFile.ts reads this same key at download time.
  showSaveAsDialog: { storageKey: SHOW_SAVE_AS_DIALOG_KEY, defaultValue: true },
  // Shrinks the Follow Up tab to the icon and count alone. Defaults OFF so the tab
  // announces itself the first time a user meets it; once they know what it is, the
  // wording is just width taken from the page behind it.
  compactFollowUpHandle: { storageKey: 'compactFollowUpHandle', defaultValue: false },
  // Hides the tab entirely until something is bookmarked. Defaults OFF: a handle
  // that is invisible until you have already used the feature cannot teach anyone
  // the feature exists.
  hideFollowUpHandleWhenEmpty: { storageKey: 'hideFollowUpHandleWhenEmpty', defaultValue: false },
};

const PREF_NAMES = Object.keys(PREFS) as PrefName[];

type PrefState = Record<PrefName, boolean>;

/** Read one preference out of localStorage, honouring its default. */
const readLocal = (name: PrefName): boolean => {
  const { storageKey, defaultValue } = PREFS[name];
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(storageKey);
  } catch {
    return defaultValue; // private mode / storage disabled
  }
  // Opt-out preferences flip only on an explicit 'false'; opt-in ones only on 'true'.
  return defaultValue ? saved !== 'false' : saved === 'true';
};

const writeLocal = (name: PrefName, value: boolean): void => {
  try {
    localStorage.setItem(PREFS[name].storageKey, value.toString());
  } catch {
    // Storage unavailable — the backend copy is still authoritative.
  }
};

const readAllLocal = (): PrefState =>
  PREF_NAMES.reduce((acc, name) => {
    acc[name] = readLocal(name);
    return acc;
  }, {} as PrefState);

interface WorkingStatusPreferenceContextType {
  showSamsaraRouteEtas: boolean;
  setShowSamsaraRouteEtas: (value: boolean) => void;
  showExtendedWorkingStatus: boolean;
  setShowExtendedWorkingStatus: (value: boolean) => void;
  defaultSendToGwiMove: boolean;
  setDefaultSendToGwiMove: (value: boolean) => void;
  planningShowAllMoves: boolean;
  setPlanningShowAllMoves: (value: boolean) => void;
  planningShowTwic: boolean;
  setPlanningShowTwic: (value: boolean) => void;
  autoCloseProOnSave: boolean;
  setAutoCloseProOnSave: (value: boolean) => void;
  showSaveAsDialog: boolean;
  setShowSaveAsDialog: (value: boolean) => void;
  compactFollowUpHandle: boolean;
  setCompactFollowUpHandle: (value: boolean) => void;
  hideFollowUpHandleWhenEmpty: boolean;
  setHideFollowUpHandleWhenEmpty: (value: boolean) => void;
}

const WorkingStatusPreferenceContext = createContext<WorkingStatusPreferenceContextType | undefined>(undefined);

export const WorkingStatusPreferenceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [prefs, setPrefs] = useState<PrefState>(readAllLocal);

  // Mirror of the live values, so a setter can persist the whole blob without
  // closing over a stale render's state.
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;

  // Hydrate from the backend once. Deliberately does NOT write back: saving here
  // would race the first render and could overwrite a stored preference with this
  // device's local default.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const setting = await userSettingsService.getUserSetting(SETTING_KEY);
        const raw = setting?.setting_value;
        if (!raw || cancelled) return;
        const stored = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!stored || typeof stored !== 'object') return;

        const next = { ...prefsRef.current };
        let changed = false;
        for (const name of PREF_NAMES) {
          // Only booleans the backend actually holds. A preference the user has
          // never touched stays on this device's value rather than being reset.
          if (typeof stored[name] === 'boolean' && stored[name] !== next[name]) {
            next[name] = stored[name];
            writeLocal(name, stored[name]);
            changed = true;
          }
        }
        if (changed && !cancelled) {
          prefsRef.current = next;
          setPrefs(next);
        }
      } catch {
        // Not signed in yet, offline, or the settings call failed — localStorage stands.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setPref = useCallback((name: PrefName, value: boolean) => {
    const next = { ...prefsRef.current, [name]: value };
    prefsRef.current = next;
    setPrefs(next);
    writeLocal(name, value);
    // Fire-and-forget: a failed save leaves the local copy correct for this device,
    // which is the behaviour these preferences had before they were backed by the
    // server at all.
    void Promise.resolve(userSettingsService.saveUserSetting(SETTING_KEY, next)).catch(() => {});
  }, []);

  const value = useMemo<WorkingStatusPreferenceContextType>(
    () => ({
      showSamsaraRouteEtas: prefs.showSamsaraRouteEtas,
      setShowSamsaraRouteEtas: v => setPref('showSamsaraRouteEtas', v),
      showExtendedWorkingStatus: prefs.showExtendedWorkingStatus,
      setShowExtendedWorkingStatus: v => setPref('showExtendedWorkingStatus', v),
      defaultSendToGwiMove: prefs.defaultSendToGwiMove,
      setDefaultSendToGwiMove: v => setPref('defaultSendToGwiMove', v),
      planningShowAllMoves: prefs.planningShowAllMoves,
      setPlanningShowAllMoves: v => setPref('planningShowAllMoves', v),
      planningShowTwic: prefs.planningShowTwic,
      setPlanningShowTwic: v => setPref('planningShowTwic', v),
      autoCloseProOnSave: prefs.autoCloseProOnSave,
      setAutoCloseProOnSave: v => setPref('autoCloseProOnSave', v),
      showSaveAsDialog: prefs.showSaveAsDialog,
      setShowSaveAsDialog: v => setPref('showSaveAsDialog', v),
      compactFollowUpHandle: prefs.compactFollowUpHandle,
      setCompactFollowUpHandle: v => setPref('compactFollowUpHandle', v),
      hideFollowUpHandleWhenEmpty: prefs.hideFollowUpHandleWhenEmpty,
      setHideFollowUpHandleWhenEmpty: v => setPref('hideFollowUpHandleWhenEmpty', v),
    }),
    [prefs, setPref],
  );

  return (
    <WorkingStatusPreferenceContext.Provider value={value}>
      {children}
    </WorkingStatusPreferenceContext.Provider>
  );
};

export const useWorkingStatusPreference = () => {
  const context = useContext(WorkingStatusPreferenceContext);
  if (context === undefined) {
    throw new Error('useWorkingStatusPreference must be used within a WorkingStatusPreferenceProvider');
  }
  return context;
};
